import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Self-contained dialog for sending a note back to the provider with a
 * correction reason. Local component state means typing in the textarea
 * only reconciles this dialog — NOT the 86k-line App tree.
 *
 * Props:
 *   open           — dialog visibility (controlled by parent)
 *   onOpenChange   — (bool) => void; called when dialog should close
 *   note           — the note being sent back. Required: { id }.
 *   currentUserId  — passed through to the API as corrections_requested_by
 *   onSuccess      — (noteId, correctionReason) => void; called after a
 *                    successful send-back so the parent can perform
 *                    optimistic list updates / close the review dialog.
 *                    Errors in this callback are swallowed (silent
 *                    console.warn) so they DO NOT show as "Failed to send".
 */
export const NeedsCorrectionsDialog = React.memo(function NeedsCorrectionsDialog({
  open,
  onOpenChange,
  note,
  currentUserId,
  onSuccess,
}) {
  const [correctionReason, setCorrectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset textarea when dialog re-opens
  useEffect(() => {
    if (!open) {
      setCorrectionReason('');
    }
  }, [open]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSubmit = useCallback(async () => {
    if (!note?.id) return;
    if (!correctionReason.trim()) {
      toast.error('Please provide a reason for corrections');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${API}/notes/${note.id}`, {
        status: 'needs_corrections',
        needs_corrections_reason: correctionReason,
        corrections_requested_at: new Date().toISOString(),
        corrections_requested_by: currentUserId,
      });
      toast.success('Note sent back for corrections');
    } catch (error) {
      console.error('Failed to send note back:', error);
      toast.error(error.response?.data?.detail || 'Failed to send note back');
      setSubmitting(false);
      return;
    }
    // Post-success — failures here MUST NOT trigger an error toast.
    try {
      onSuccess?.(note, correctionReason);
      onOpenChange(false);
    } catch (e) {
      console.warn('Post-corrections cleanup failed (note was sent back successfully):', e);
    }
    setSubmitting(false);
  }, [note, correctionReason, currentUserId, onSuccess, onOpenChange]);

  const trimmedReason = correctionReason.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Block dismissal while submitting (Escape / click-outside / X)
        if (submitting && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg" aria-busy={submitting}>
        <DialogHeader>
          <DialogTitle>Send Note Back for Corrections</DialogTitle>
          <DialogDescription>
            Provide detailed feedback about what needs to be corrected in this note.
          </DialogDescription>
        </DialogHeader>
        <div className={`space-y-4 transition-opacity ${submitting ? 'opacity-60 pointer-events-none' : ''}`}>
          <div>
            <Label htmlFor="correction-reason">Reason for Correction *</Label>
            <textarea
              id="correction-reason"
              data-testid="needs-corrections-reason-textarea"
              className="w-full mt-2 p-3 border rounded-md min-h-[120px] disabled:bg-gray-50"
              placeholder="Please describe what needs to be corrected (e.g., missing CPT code, incorrect diagnosis, incomplete documentation)..."
              value={correctionReason}
              onChange={(e) => setCorrectionReason(e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-gray-500 mt-1">
              This message will be sent to the provider
            </p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={handleCancel}
            data-testid="needs-corrections-cancel-btn"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-orange-600 hover:bg-orange-700 min-w-[160px]"
            data-testid="needs-corrections-submit-btn"
            disabled={!trimmedReason || submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending back…
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Back
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
});

export default NeedsCorrectionsDialog;
